import Dialog from 'components/ReusableComponents/Dialog'
import { useEffect, useState } from 'react'

import { ReactComponent as CloseIcon } from '../ConfirmationDialog/close.svg'
import styles from './Tutorial.module.scss'
import {
  getInitialTutorialStepIndex,
  getNextTutorialStepIndex,
  getPreviousTutorialStepIndex,
  isLastTutorialStep
} from './tutorialStepNavigation'

function preloadTutorialImages(steps = []) {
  steps.forEach(step => {
    if (!step?.imageSrc) {
      return
    }

    const image = new Image()
    image.src = step.imageSrc
  })
}

const Tutorial = ({ isOpen, onClose, title, steps = [] }) => {
  const [stepIndex, setStepIndex] = useState(getInitialTutorialStepIndex)

  useEffect(() => {
    if (isOpen) {
      setStepIndex(getInitialTutorialStepIndex())
      preloadTutorialImages(steps)
    }
  }, [isOpen, steps])

  useEffect(() => {
    if (!isOpen || !onClose) {
      return undefined
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !steps.length) {
    return null
  }

  const totalSteps = steps.length
  const currentStep = steps[stepIndex] || {}
  const isFirstStep = stepIndex === 0
  const isLastStep = isLastTutorialStep(stepIndex, totalSteps)

  function handleBack() {
    setStepIndex(current => getPreviousTutorialStepIndex(current))
  }

  function handleNext() {
    if (isLastStep) {
      onClose()
      return
    }

    setStepIndex(current => getNextTutorialStepIndex(current, totalSteps))
  }

  return (
    <Dialog verticalPadding={24}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close tutorial"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.stepCounter}>
            Step {stepIndex + 1} of {totalSteps}
          </p>
          {currentStep.title && <h3 className={styles.stepTitle}>{currentStep.title}</h3>}
          {currentStep.description && (
            <p className={styles.stepDescription}>{currentStep.description}</p>
          )}

          <div className={styles.media}>
            {steps.map((step, index) => {
              if (!step.imageSrc) {
                if (index !== stepIndex) {
                  return null
                }

                return (
                  <div
                    key={`placeholder-${index}`}
                    className={styles.imagePlaceholder}
                    aria-hidden="true"
                  >
                    <span>Screenshot placeholder</span>
                  </div>
                )
              }

              const isActive = index === stepIndex

              return (
                <img
                  key={step.imageSrc}
                  className={[styles.image, isActive ? '' : styles.imageHidden]
                    .filter(Boolean)
                    .join(' ')}
                  src={step.imageSrc}
                  alt={isActive ? step.imageAlt || step.title || title : ''}
                  aria-hidden={!isActive}
                  loading="eager"
                  decoding="async"
                />
              )
            })}
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleBack}
            disabled={isFirstStep}
          >
            Back
          </button>
          <button type="button" className={styles.primaryButton} onClick={handleNext}>
            {isLastStep ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

export default Tutorial
